import { ProfilePage } from "components/ProfilePage"
import { Button } from "components/Button"
import { AdvertInteractionsProvider } from "domains/advert/AdvertInteractionsProvider"
import { useTranslator } from "components/Translator"
import { Text } from "components/Text"
import { useConnectedUser } from "components/ConnectedUserProvider"
import { useRouter } from "components/Router"
import cx from "classnames"
import * as AdvertBrowsingMode from "enums/AdvertBrowsingMode"
import * as UserType from "enums/UserType"
import { AdvertsCalendar } from "domains/advert/AdvertsCalendar"
import { Tab, Tabs, TabList } from "components/Tab"
import { AdvertResult } from "components/SearchResult"
import { AdvertAddButton } from "domains/advert/AdvertAddButton"

export function AccountAdvertIndexPage({
  user,
  adverts,
  tabs = window.accountNav,
  infos,
  browsingMode,
}) {
  const translator = useTranslator()
  const connectedUser = useConnectedUser()
  const router = useRouter()

  var viewTabs = [
    {
      label: translator.trans(
        "account.advert.index.tabs.calendar",
        null,
        "pages",
      ),
      id: "calendar",
      href: router.generate("app_account_advert_index", {
        browsingMode: AdvertBrowsingMode.CALENDAR,
        userId: user.id,
      }),
      isCurrent: browsingMode === AdvertBrowsingMode.CALENDAR,
    },
    {
      label: translator.trans(
        user.type && UserType.CREW !== user.type.enum_value
          ? "account.advert.index.tabs.all"
          : "account.advert.index.tabs.list",
        null,
        "pages",
      ),
      id: "list",
      href: router.generate("app_account_advert_index", {
        browsingMode: AdvertBrowsingMode.LIST,
        userId: user.id,
      }),
      isCurrent: browsingMode === AdvertBrowsingMode.LIST,
    },
  ]

  const ownerSpecificTabs = [
    {
      label: translator.trans(
        "account.advert.index.tabs.not_completed",
        null,
        "pages",
      ),
      id: "notCompleted",
      href: router.generate("app_account_advert_index", {
        browsingMode: AdvertBrowsingMode.NOT_COMPLETED,
        userId: user.id,
      }),
      isCurrent: browsingMode === AdvertBrowsingMode.NOT_COMPLETED,
    },
    {
      label: translator.trans(
        "account.advert.index.tabs.completed",
        null,
        "pages",
      ),
      id: "completed",
      href: router.generate("app_account_advert_index", {
        browsingMode: AdvertBrowsingMode.COMPLETED,
        userId: user.id,
      }),
      isCurrent: browsingMode === AdvertBrowsingMode.COMPLETED,
    },
  ]

  if (user.type && UserType.CREW !== user.type.enum_value) {
    viewTabs = viewTabs.concat(ownerSpecificTabs)
  }

  const isOwnProfile = connectedUser?.id === user.id

  const extraActions = [
    isOwnProfile ? <AdvertAddButton key="advert-add" /> : null,
    isOwnProfile ? (
      <Button
        key="adverts-manage"
        variant="outline"
        color="secondary"
        href={router.generate("app_user_advert_index")}
      >
        {translator.trans("account.advert.index.manageCTA", null, "pages")}
      </Button>
    ) : null,
  ].filter(Boolean)

  return (
    <ProfilePage
      user={user}
      tabs={tabs}
      infos={infos}
      extraActions={extraActions}
    >
      <div className="space-y-6">
        <Tabs
          currentPanel={viewTabs.find((tab) => tab.isCurrent).id}
          onChange={() => null}
        >
          <TabList>
            {viewTabs.map((tab) => (
              <Tab
                key={tab.label}
                label={tab.label}
                id={tab.id}
                href={tab.href}
              />
            ))}
          </TabList>
        </Tabs>
        {browsingMode !== AdvertBrowsingMode.CALENDAR ? (
          adverts.length > 0 ? (
            <ListView adverts={adverts} />
          ) : (
            <Text tag="p">
              {translator.trans(
                "account.advert.index.emptyMessage",
                null,
                "pages",
              )}
            </Text>
          )
        ) : adverts.length > 0 ? (
          <CalendarView adverts={adverts} user={user} />
        ) : (
          <Text tag="p">
            {translator.trans(
              "account.advert.index.emptyMessage",
              null,
              "pages",
            )}
          </Text>
        )}
      </div>
    </ProfilePage>
  )
}

const ListView = ({ adverts }) => {
  return (
    <AdvertInteractionsProvider ids={adverts.map((advert) => advert.id)}>
      <div
        className={cx({
          "grid grid-cols-1 lg:grid-cols-2 gap-6": adverts.length > 0,
        })}
      >
        {adverts.map((advert) => {
          return <AdvertResult key={advert.id} advert={advert} />
        })}
      </div>
    </AdvertInteractionsProvider>
  )
}

const CalendarView = ({ adverts, user }) => {
  const router = useRouter()

  const goToAdvertPage = (advert) => {
    router.redirect({
      name: "app_account_advert_show",
      params: { userId: user.id, advertId: advert.id },
    })
  }

  return <AdvertsCalendar adverts={adverts} onSelectAdvert={goToAdvertPage} />
}
